import { type Href } from 'expo-router';
import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    Checkbox,
    CheckCircle2Icon,
    ChevronDownIcon,
    ChevronRightIcon,
    CircleIcon,
    CopyIcon,
    DownloadIcon,
    FootnoteRegular,
    H5Emphasized,
    H6Emphasized,
    Icon,
    IconButton,
    Input,
    MailIcon,
    PlayIcon,
    ServerIcon,
    Spinner,
    UploadIcon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { centsToFormattedDollars } from '../../common/utils/formatting';
import { adminUserAtom } from '../../entities/entityAtoms';
import {
    useGetIctReconStatus,
    useManualResolution,
    useSendWireEmail,
    useTriggerBatchRecon,
    useUploadReconFile,
    useUploadToApexSftp,
    useUploadToSyncteraSftp,
} from '../hooks/useIctData';
import type {
    AdminICTReconStatusDto,
    IctReconStatus,
    ReconBreakDto,
    ReconWireDirection,
} from '../types/ImOpsTypes';

interface ImOpsDailyReconPanelProps {
    settlementDate: string;
}

const STATUS_CONFIG: Record<IctReconStatus, { label: string; color: string }> =
    {
        IN_PROGRESS: { label: 'Processing...', color: '#2196F3' },
        PENDING_REVIEW: { label: 'Pending Review', color: '#FF9800' },
        COMPLETED: { label: 'Completed', color: '#28a745' },
        COMPLETED_MANUAL: { label: 'Completed (Manual)', color: '#28a745' },
        COMPLETED_NO_ACTIVITY: { label: 'No Activity', color: '#6c757d' },
        COMPLETED_WEEKEND_OR_HOLIDAY: {
            label: 'Weekend/Holiday',
            color: '#6c757d',
        },
        FAILED: { label: 'Failed', color: '#dc3545' },
    };

const getWireDirectionLabel = (direction: ReconWireDirection): string => {
    switch (direction) {
        case 'OUTGOING_WIRE_EB_TO_APEX':
            return 'Outgoing to Apex';
        case 'INCOMING_WIRE_APEX_TO_EB':
            return 'Incoming from Apex';
        case 'NO_WIRE':
            return 'No wire needed';
    }
};

const formatTimestamp = (iso: string | null): string => {
    if (iso == null) return '\u2014';
    return new Date(iso).toLocaleString();
};

const isReadOnly = (status: IctReconStatus): boolean =>
    status !== 'PENDING_REVIEW';

const isTerminal = (status: IctReconStatus): boolean =>
    status.startsWith('COMPLETED');

/**
 * Split a comma-separated recipient list. Entries may be bare emails or
 * `Name <email>` — commas only appear as separators between entries.
 */
const parseEmailList = (value: string): string[] =>
    value
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean);

const extractEmailAddress = (entry: string): string => {
    const angled = /<([^>]+)>/.exec(entry);
    return (angled?.[1] ?? entry).trim().toLowerCase();
};

/** Normalize `Name <email>` / bare address lists to bare emails for the mark-sent API. */
const toBareEmails = (entries: string[]): string[] =>
    entries
        .map((entry) => {
            const angled = /<([^>]+)>/.exec(entry);
            return (angled?.[1] ?? entry).trim();
        })
        .filter(Boolean);

/** Drop the logged-in Grove user from CC so they are not CCing themselves. */
const excludeSenderFromCc = (
    ccEntries: string[],
    senderEmail: string,
): string[] => {
    const sender = senderEmail.trim().toLowerCase();
    if (sender === '') return ccEntries;
    return ccEntries.filter((entry) => extractEmailAddress(entry) !== sender);
};

/** Must match backend ICT_WIRE_EMAIL_CALLBACK_PLACEHOLDER. */
const CALL_BACK_NUMBER_PLACEHOLDER = '{{CALL_BACK_NUMBER}}';

/**
 * Optional call-back numbers by Grove admin email. AdminUser has no phone field;
 * extend this map as IM ops numbers are confirmed.
 */
const ICT_WIRE_CALLBACK_BY_EMAIL: Record<string, string> = {
    'bob@fruitful.co': '973-723-3966',
    'sam@fruitful.co': '914-646-2284',
    'john@fruitful.co': '913-744-5462',
    'neczar@fruitful.co': '707-704-9413',
    'jill@fruitful.co': '651-216-8748',
};

const resolveCallBackNumber = (adminUserEmail: string): string =>
    ICT_WIRE_CALLBACK_BY_EMAIL[adminUserEmail.trim().toLowerCase()] ?? '';

const applyCallBackNumber = (body: string, callBackNumber: string): string => {
    if (!body.includes(CALL_BACK_NUMBER_PLACEHOLDER)) {
        return body;
    }
    return body.split(CALL_BACK_NUMBER_PLACEHOLDER).join(callBackNumber.trim());
};

const buildGmailComposeUrl = ({
    to,
    cc,
    subject,
    body,
}: {
    to: string[];
    cc: string[];
    subject: string;
    body: string;
}): string => {
    const params = new URLSearchParams();
    params.set('view', 'cm');
    params.set('fs', '1');
    if (to.length > 0) params.set('to', to.join(', '));
    if (cc.length > 0) params.set('cc', cc.join(', '));
    if (subject !== '') params.set('su', subject);
    if (body !== '') params.set('body', body);
    return `https://mail.google.com/mail/?${params.toString()}`;
};

// --- Sub-components ---

const CopyFieldButton = ({
    value,
    label,
}: {
    value: string;
    label: string;
}) => {
    const toast = useToastController();
    return (
        <IconButton
            variant="tertiary"
            size="tiny"
            icon={CopyIcon}
            disabled={value.trim() === ''}
            onPress={() => {
                void navigator.clipboard.writeText(value).then(() => {
                    toast.show(`${label} copied`, { duration: 2000 });
                });
            }}
        />
    );
};

const StatusBadge = ({ status }: { status: IctReconStatus }) => {
    const { label, color } = STATUS_CONFIG[status];
    return (
        <XStack
            backgroundColor={`${color}18`}
            paddingHorizontal={14}
            paddingVertical={4}
            borderRadius={14}
            borderWidth={1.5}
            borderColor={color}
        >
            <BodySmallEmphasized color={color}>{label}</BodySmallEmphasized>
        </XStack>
    );
};

const OperationCard = ({
    title,
    icon,
    isDone,
    timestamp,
    detail,
    actionLabel,
    onAction,
    isPending,
    disabled,
    alwaysShowAction,
}: {
    title: string;
    icon: React.ReactNode;
    isDone: boolean;
    timestamp: string | null;
    detail?: string | null;
    actionLabel: string;
    onAction: () => void;
    isPending: boolean;
    disabled: boolean;
    alwaysShowAction?: boolean;
}) => (
    <YStack
        flex={1}
        flexBasis={0}
        padding={16}
        borderRadius={12}
        borderWidth={1}
        borderColor={isDone ? '$green300' : '$gray200'}
        backgroundColor={isDone ? '#f0fdf4' : 'white'}
        gap={12}
    >
        <XStack justifyContent="space-between" alignItems="center">
            <XStack gap={8} alignItems="center">
                {icon}
                <BodySmallEmphasized>{title}</BodySmallEmphasized>
            </XStack>
            {isDone ? (
                <Icon icon={CheckCircle2Icon} size={20} color="$green1000" />
            ) : (
                <Icon icon={CircleIcon} size={20} color="$gray200" />
            )}
        </XStack>

        {isDone && timestamp != null && (
            <BodySmallRegular color="$gray1000" fontSize={11}>
                {formatTimestamp(timestamp)}
            </BodySmallRegular>
        )}

        {detail != null && (
            <BodySmallRegular
                color="$gray1000"
                fontSize={11}
                fontFamily="monospace"
            >
                {detail}
            </BodySmallRegular>
        )}

        {isDone && !alwaysShowAction ? null : (
            <Button
                variant={isDone ? 'secondary' : 'primary'}
                size="small"
                onPress={onAction}
                disabled={disabled || isPending}
            >
                {isPending ? 'Processing...' : actionLabel}
            </Button>
        )}
    </YStack>
);

// --- Wire Email Modal ---

const WireEmailModal = ({
    recon,
    isOpen,
    onClose,
    onMarkSent,
    isMarkingSent,
    adminUserEmail,
}: {
    recon: AdminICTReconStatusDto;
    isOpen: boolean;
    onClose: () => void;
    onMarkSent: (
        overrides?: {
            subject: string;
            body: string;
            recipients: string[];
            cc: string[];
        },
        review?: {
            reviewedBy: string;
            reviewNotes: string;
        },
    ) => void;
    isMarkingSent: boolean;
    adminUserEmail: string;
}) => {
    const emailSent = recon.emailSentAt != null;
    const defaultRecipients = recon.emailDefaultRecipients ?? [];
    const defaultCc = excludeSenderFromCc(
        recon.emailDefaultCc ?? [],
        adminUserEmail,
    );

    const [editUnlocked, setEditUnlocked] = useState(false);
    const [subject, setSubject] = useState(recon.emailSubject ?? '');
    const [body, setBody] = useState(recon.emailBody ?? '');
    const [recipients, setRecipients] = useState(
        recon.emailSentTo?.join(', ') ?? defaultRecipients.join(', '),
    );
    const [cc, setCc] = useState(
        recon.emailSentCc?.join(', ') ?? defaultCc.join(', '),
    );
    const [reviewedBy, setReviewedBy] = useState(adminUserEmail);
    const [reviewNotes, setReviewNotes] = useState('');
    const [callBackNumber, setCallBackNumber] = useState(
        resolveCallBackNumber(adminUserEmail),
    );

    const draftNeedsCallBack =
        (recon.emailBody ?? '').includes(CALL_BACK_NUMBER_PLACEHOLDER) ||
        recon.wireDirection === 'OUTGOING_WIRE_EB_TO_APEX';

    useEffect(() => {
        if (isOpen) {
            const initialCc = excludeSenderFromCc(
                recon.emailDefaultCc ?? [],
                adminUserEmail,
            );
            setEditUnlocked(false);
            setSubject(recon.emailSubject ?? '');
            setBody(recon.emailBody ?? '');
            setRecipients(
                recon.emailSentTo?.join(', ') ??
                    (recon.emailDefaultRecipients ?? []).join(', '),
            );
            setCc(recon.emailSentCc?.join(', ') ?? initialCc.join(', '));
            setReviewedBy(recon.reviewedBy ?? adminUserEmail);
            setReviewNotes(recon.reviewNotes ?? '');
            setCallBackNumber(resolveCallBackNumber(adminUserEmail));
        }
    }, [isOpen, recon, adminUserEmail]);

    const effectiveSubject = editUnlocked
        ? subject
        : (recon.emailSubject ?? '');
    const rawBody = editUnlocked ? body : (recon.emailBody ?? '');
    const effectiveBody = applyCallBackNumber(rawBody, callBackNumber);
    const effectiveTo = editUnlocked
        ? parseEmailList(recipients)
        : (recon.emailSentTo ?? defaultRecipients);
    const effectiveCc = editUnlocked
        ? parseEmailList(cc)
        : (recon.emailSentCc ?? defaultCc);

    const handleOpenInGmail = () => {
        const url = buildGmailComposeUrl({
            to: effectiveTo,
            cc: effectiveCc,
            subject: effectiveSubject,
            body: effectiveBody,
        });
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleMarkSent = () => {
        const review = {
            reviewedBy,
            reviewNotes,
        };
        // API stores bare emails; Gmail compose can keep `Name <email>` display form.
        onMarkSent(
            {
                subject: editUnlocked ? subject : effectiveSubject,
                body: effectiveBody,
                recipients: toBareEmails(
                    editUnlocked ? parseEmailList(recipients) : effectiveTo,
                ),
                cc: toBareEmails(
                    editUnlocked ? parseEmailList(cc) : effectiveCc,
                ),
            },
            review,
        );
    };

    const breaks = recon.breaks ?? [];
    const displayTo =
        effectiveTo.length > 0 ? effectiveTo.join(', ') : '(no recipients)';
    const displayCc =
        effectiveCc.length > 0 ? effectiveCc.join(', ') : '(none)';

    return (
        <Dialog
            visible={isOpen}
            onHide={onClose}
            header={
                emailSent ? 'Wire Email — Marked Sent' : 'Wire Email — Compose'
            }
            style={{ width: '700px' }}
        >
            <YStack gap={20} padding={8}>
                {!emailSent && (
                    <BodySmallRegular color="$gray1000">
                        Open the draft in Gmail, send from your account, then
                        mark as sent here so reconciliation can complete.
                    </BodySmallRegular>
                )}

                {/* Subject */}
                <YStack gap={4}>
                    <XStack
                        alignItems="center"
                        justifyContent="space-between"
                        gap={8}
                    >
                        <BodySmallEmphasized>Subject</BodySmallEmphasized>
                        <CopyFieldButton
                            value={effectiveSubject}
                            label="Subject"
                        />
                    </XStack>
                    {emailSent || !editUnlocked ? (
                        <BodySmallRegular fontFamily="monospace">
                            {effectiveSubject !== ''
                                ? effectiveSubject
                                : '(no subject)'}
                        </BodySmallRegular>
                    ) : (
                        <Input
                            value={subject}
                            onChangeText={setSubject}
                            placeholder="Email subject"
                        />
                    )}
                </YStack>

                {/* Body */}
                <YStack gap={4}>
                    <XStack
                        alignItems="center"
                        justifyContent="space-between"
                        gap={8}
                    >
                        <BodySmallEmphasized>Body</BodySmallEmphasized>
                        <CopyFieldButton value={effectiveBody} label="Body" />
                    </XStack>
                    {emailSent || !editUnlocked ? (
                        <YStack
                            backgroundColor="$gray100"
                            padding={12}
                            borderRadius={8}
                            maxHeight={300}
                            overflow="scroll"
                        >
                            <BodySmallRegular
                                style={{
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word',
                                }}
                            >
                                {effectiveBody !== ''
                                    ? effectiveBody
                                    : '(no body)'}
                            </BodySmallRegular>
                        </YStack>
                    ) : (
                        <textarea
                            value={body}
                            onChange={(e) => {
                                setBody(e.target.value);
                            }}
                            rows={12}
                            style={{
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                padding: 12,
                                borderRadius: 8,
                                border: '1px solid #ccc',
                                resize: 'vertical',
                            }}
                        />
                    )}
                </YStack>

                {!emailSent && draftNeedsCallBack && (
                    <YStack gap={4}>
                        <BodySmallEmphasized>
                            Call back number
                        </BodySmallEmphasized>
                        <Input
                            value={callBackNumber}
                            onChangeText={setCallBackNumber}
                            placeholder="e.g. 707-704-9413"
                            size="tiny"
                        />
                        <BodySmallRegular color="$gray1000" fontSize={11}>
                            Required for outgoing wires. Prefills from your
                            Grove email when a default is configured.
                        </BodySmallRegular>
                    </YStack>
                )}

                {/* Recipients */}
                <YStack gap={4}>
                    <XStack
                        alignItems="center"
                        justifyContent="space-between"
                        gap={8}
                    >
                        <BodySmallEmphasized>To</BodySmallEmphasized>
                        <CopyFieldButton
                            value={effectiveTo.join(', ')}
                            label="To"
                        />
                    </XStack>
                    {emailSent || !editUnlocked ? (
                        <BodySmallRegular>{displayTo}</BodySmallRegular>
                    ) : (
                        <Input
                            value={recipients}
                            onChangeText={setRecipients}
                            placeholder="Comma-separated emails"
                        />
                    )}
                </YStack>

                {/* CC */}
                <YStack gap={4}>
                    <XStack
                        alignItems="center"
                        justifyContent="space-between"
                        gap={8}
                    >
                        <BodySmallEmphasized>CC</BodySmallEmphasized>
                        <CopyFieldButton
                            value={effectiveCc.join(', ')}
                            label="CC"
                        />
                    </XStack>
                    {emailSent || !editUnlocked ? (
                        <BodySmallRegular>{displayCc}</BodySmallRegular>
                    ) : (
                        <Input
                            value={cc}
                            onChangeText={setCc}
                            placeholder="Comma-separated emails"
                        />
                    )}
                </YStack>

                {/* Sent timestamp (sent mode only) */}
                {emailSent && (
                    <YStack gap={4}>
                        <BodySmallEmphasized>Marked Sent</BodySmallEmphasized>
                        <BodySmallRegular color="$green1000">
                            {formatTimestamp(recon.emailSentAt)}
                        </BodySmallRegular>
                    </YStack>
                )}

                {/* Edit lock (draft mode only) */}
                {!emailSent && (
                    <YStack
                        gap={8}
                        backgroundColor={
                            editUnlocked ? '$orange300' : '$gray100'
                        }
                        padding={12}
                        borderRadius={8}
                    >
                        <XStack gap={8} alignItems="center">
                            <Checkbox
                                id="edit-email-unlock"
                                checked={editUnlocked}
                                onCheckedChange={(checked) => {
                                    setEditUnlocked(checked === true);
                                }}
                            />
                            <BodySmallEmphasized>
                                Edit email content
                            </BodySmallEmphasized>
                        </XStack>
                        <BodySmallRegular
                            color={editUnlocked ? '$orange1100' : '$gray1000'}
                            fontSize={11}
                        >
                            {editUnlocked
                                ? 'Fields are now editable. Changes apply to Open in Gmail and Mark as sent.'
                                : 'These fields are auto-generated from the reconciliation. Check the box to override.'}
                        </BodySmallRegular>
                    </YStack>
                )}

                {/* Review section */}
                <YStack
                    gap={12}
                    padding={12}
                    borderRadius={8}
                    borderWidth={1}
                    borderColor="$gray200"
                    backgroundColor="white"
                >
                    <BodySmallEmphasized>Review</BodySmallEmphasized>
                    {emailSent ? (
                        <YStack gap={8}>
                            <XStack gap={16}>
                                <YStack gap={2} flex={1}>
                                    <BodySmallRegular
                                        color="$gray1000"
                                        fontSize={11}
                                    >
                                        Reviewed By
                                    </BodySmallRegular>
                                    <BodySmallRegular>
                                        {recon.reviewedBy ?? '\u2014'}
                                    </BodySmallRegular>
                                </YStack>
                                <YStack gap={2} flex={1}>
                                    <BodySmallRegular
                                        color="$gray1000"
                                        fontSize={11}
                                    >
                                        Reviewed At
                                    </BodySmallRegular>
                                    <BodySmallRegular>
                                        {formatTimestamp(recon.reviewedAt)}
                                    </BodySmallRegular>
                                </YStack>
                            </XStack>
                            {recon.reviewNotes != null && (
                                <YStack gap={2}>
                                    <BodySmallRegular
                                        color="$gray1000"
                                        fontSize={11}
                                    >
                                        Notes
                                    </BodySmallRegular>
                                    <BodySmallRegular>
                                        {recon.reviewNotes}
                                    </BodySmallRegular>
                                </YStack>
                            )}
                        </YStack>
                    ) : (
                        <YStack gap={8}>
                            <YStack gap={4}>
                                <BodySmallRegular
                                    color="$gray1000"
                                    fontSize={11}
                                >
                                    Reviewed By
                                </BodySmallRegular>
                                <Input
                                    value={reviewedBy}
                                    onChangeText={setReviewedBy}
                                    placeholder="Your email"
                                    size="tiny"
                                />
                            </YStack>
                            <YStack gap={4}>
                                <BodySmallRegular
                                    color="$gray1000"
                                    fontSize={11}
                                >
                                    Review Notes (optional)
                                </BodySmallRegular>
                                <Input
                                    value={reviewNotes}
                                    onChangeText={setReviewNotes}
                                    placeholder="Any notes about this review..."
                                    size="tiny"
                                />
                            </YStack>
                        </YStack>
                    )}
                </YStack>

                {/* Breaks section */}
                {breaks.length > 0 && (
                    <YStack gap={8}>
                        <BodySmallEmphasized color="$orange1000">
                            Reconciliation Breaks ({breaks.length})
                        </BodySmallEmphasized>
                        <DataTable
                            value={breaks}
                            size="small"
                            tableStyle={{ fontSize: '0.75rem' }}
                        >
                            <Column
                                field="externalId"
                                header="External ID"
                                style={{ fontSize: '0.7rem' }}
                                body={(row: ReconBreakDto) => (
                                    <BodySmallRegular
                                        fontFamily="monospace"
                                        fontSize="0.7rem"
                                    >
                                        {row.externalId}
                                    </BodySmallRegular>
                                )}
                            />
                            <Column
                                field="source"
                                header="Source"
                                style={{ fontSize: '0.7rem' }}
                            />
                            <Column
                                field="breakReason"
                                header="Reason"
                                style={{ fontSize: '0.7rem' }}
                            />
                            <Column
                                field="amount"
                                header="Amount"
                                style={{ fontSize: '0.7rem' }}
                                body={(row: ReconBreakDto) =>
                                    `$${row.amount.toFixed(2)}`
                                }
                            />
                            <Column
                                field="transferDirection"
                                header="Direction"
                                style={{ fontSize: '0.7rem' }}
                            />
                        </DataTable>
                    </YStack>
                )}

                {/* Actions */}
                <XStack gap={12} justifyContent="flex-end" flexWrap="wrap">
                    <Button variant="secondary" size="small" onPress={onClose}>
                        {emailSent ? 'Close' : 'Cancel'}
                    </Button>
                    {!emailSent && (
                        <>
                            <Button
                                variant="secondary"
                                size="small"
                                onPress={handleOpenInGmail}
                                disabled={
                                    effectiveSubject === '' ||
                                    effectiveBody === '' ||
                                    (draftNeedsCallBack &&
                                        callBackNumber.trim() === '') ||
                                    effectiveBody.includes(
                                        CALL_BACK_NUMBER_PLACEHOLDER,
                                    )
                                }
                            >
                                Open in Gmail
                            </Button>
                            <Button
                                variant="primary"
                                size="small"
                                onPress={handleMarkSent}
                                disabled={
                                    isMarkingSent ||
                                    effectiveSubject === '' ||
                                    effectiveBody === '' ||
                                    effectiveTo.length === 0 ||
                                    (draftNeedsCallBack &&
                                        callBackNumber.trim() === '') ||
                                    effectiveBody.includes(
                                        CALL_BACK_NUMBER_PLACEHOLDER,
                                    )
                                }
                            >
                                {isMarkingSent ? 'Marking...' : 'Mark as Sent'}
                            </Button>
                        </>
                    )}
                </XStack>
            </YStack>
        </Dialog>
    );
};

// --- Main Component ---

export const ImOpsDailyReconPanel = ({
    settlementDate,
}: ImOpsDailyReconPanelProps) => {
    const [showTriggerDialog, setShowTriggerDialog] = useState(false);
    const [isDryRun, setIsDryRun] = useState(false);
    const [showManualDialog, setShowManualDialog] = useState(false);
    const [manualNotes, setManualNotes] = useState('');
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [isStateLogExpanded, setIsStateLogExpanded] = useState(false);

    const adminUser = useAtomValue(adminUserAtom);
    const toast = useToastController();

    const {
        data: recon,
        isLoading,
        refetch,
    } = useGetIctReconStatus({ settlementDate });

    const triggerReconMutation = useTriggerBatchRecon();
    const sendEmailMutation = useSendWireEmail();
    const uploadApexMutation = useUploadToApexSftp();
    const uploadSyncteraMutation = useUploadToSyncteraSftp();
    const uploadReconFileMutation = useUploadReconFile();
    const manualResolutionMutation = useManualResolution();

    const apexFileInputRef = useRef<HTMLInputElement>(null);
    const syncteraFileInputRef = useRef<HTMLInputElement>(null);
    const [showReuploadConfirm, setShowReuploadConfirm] = useState<
        'apex' | 'synctera' | null
    >(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);

    // --- Mutation helpers ---

    const runMutation = async (
        label: string,
        mutate: () => Promise<{ success: boolean; message: string }>,
    ) => {
        try {
            const result = await mutate();
            if (result.success) {
                toast.show(
                    `${label}: ${result.message}`,
                    MutationSuccessToastOptions,
                );
            } else {
                toast.show(
                    `${label} failed`,
                    MutationErrorToastOptions(result.message),
                );
            }
            void refetch();
        } catch (error) {
            toast.show(`${label} error`, MutationErrorToastOptions(error));
        }
    };

    const handleMarkWireEmailSent = async (
        overrides?: {
            subject: string;
            body: string;
            recipients: string[];
            cc: string[];
        },
        review?: {
            reviewedBy: string;
            reviewNotes: string;
        },
    ) => {
        await runMutation(
            'Mark wire email sent',
            async () =>
                await sendEmailMutation.mutateAsync({
                    body: {
                        settlementDate,
                        ...(overrides != null && {
                            subject: overrides.subject,
                            body: overrides.body,
                            recipients: overrides.recipients,
                            cc: overrides.cc,
                        }),
                        ...(review != null && {
                            reviewedBy: review.reviewedBy,
                            reviewNotes: review.reviewNotes,
                        }),
                    },
                }),
        );
        setShowEmailModal(false);
    };

    const handleUploadApex = async () => {
        await runMutation(
            'Upload to Apex SFTP',
            async () =>
                await uploadApexMutation.mutateAsync({
                    body: { settlementDate },
                }),
        );
    };

    const handleUploadSynctera = async () => {
        await runMutation(
            'Upload to Synctera SFTP',
            async () =>
                await uploadSyncteraMutation.mutateAsync({
                    body: { settlementDate },
                }),
        );
    };

    const handleFileSelected = useCallback(
        (fileType: 'apex' | 'synctera', file: File) => {
            setPendingFile(file);
            setShowReuploadConfirm(fileType);
        },
        [],
    );

    const handleConfirmReupload = () => {
        if (showReuploadConfirm == null || pendingFile == null) return;

        const fileType = showReuploadConfirm;
        const file = pendingFile;
        setShowReuploadConfirm(null);
        setPendingFile(null);

        const reader = new FileReader();
        reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1];
            if (base64 == null) {
                toast.show(
                    'Re-upload error',
                    MutationErrorToastOptions('Failed to read file'),
                );
                return;
            }
            void runMutation(
                `Re-upload ${fileType} file`,
                async () =>
                    await uploadReconFileMutation.mutateAsync({
                        body: {
                            settlementDate,
                            fileType,
                            fileContentBase64: base64,
                        },
                    }),
            );
        };
        reader.onerror = () => {
            toast.show(
                'Re-upload error',
                MutationErrorToastOptions('Failed to read file'),
            );
        };
        reader.readAsDataURL(file);
    };

    const handleTriggerRecon = async () => {
        try {
            const result = await triggerReconMutation.mutateAsync({
                body: { settlementDate, dryRun: isDryRun },
            });
            if (result.success) {
                toast.show(result.message, MutationSuccessToastOptions);
            } else {
                toast.show(
                    result.message ?? 'Reconciliation failed',
                    MutationErrorToastOptions(result.error ?? ''),
                );
            }
            setShowTriggerDialog(false);
            setIsDryRun(false);
            void refetch();
        } catch (error) {
            toast.show(
                'Reconciliation error',
                MutationErrorToastOptions(error),
            );
        }
    };

    const handleManualResolution = async () => {
        try {
            const result = await manualResolutionMutation.mutateAsync({
                body: {
                    settlementDate,
                    adminUserEmail: adminUser.email,
                    reviewNotes: manualNotes || undefined,
                },
            });
            if (result.success) {
                toast.show(result.message, MutationSuccessToastOptions);
            } else {
                toast.show(
                    'Manual resolution failed',
                    MutationErrorToastOptions(result.message),
                );
            }
            setShowManualDialog(false);
            setManualNotes('');
            void refetch();
        } catch (error) {
            toast.show(
                'Manual resolution error',
                MutationErrorToastOptions(error),
            );
        }
    };

    // --- Loading / empty states ---

    if (isLoading) {
        return (
            <YStack gap={16} paddingVertical={24}>
                <H6Emphasized>Daily Reconciliation</H6Emphasized>
                <Spinner />
            </YStack>
        );
    }

    if (recon?.status == null) {
        const notFound = recon as { message?: string } | undefined;
        const message =
            notFound?.message ??
            `No reconciliation record for ${settlementDate}`;
        return (
            <YStack gap={16}>
                <XStack justifyContent="space-between" alignItems="center">
                    <H6Emphasized>Daily Reconciliation</H6Emphasized>
                    <Button
                        variant="primary"
                        size="small"
                        onPress={() => {
                            setShowTriggerDialog(true);
                        }}
                        icon={PlayIcon}
                    >
                        Trigger Batch Recon
                    </Button>
                </XStack>
                <YStack
                    backgroundColor="#fff8e1"
                    padding={24}
                    borderRadius={12}
                    alignItems="center"
                    gap={8}
                    borderWidth={1}
                    borderColor="#FF9800"
                >
                    <BodyMediumRegular color="#e65100">
                        {message}
                    </BodyMediumRegular>
                </YStack>
                {renderTriggerDialog()}
            </YStack>
        );
    }

    // --- Derived state ---

    const status = recon.status;
    const readonly = isReadOnly(status);
    const emailDone = recon.emailSentAt != null;
    const apexSftpDone = recon.apexSftpUploadStatus === 'UPLOADED';
    const syncteraSftpDone = recon.syncteraSftpUploadStatus === 'UPLOADED';

    const hasReports =
        recon.apexReportS3Uri != null || recon.syncteraReportS3Uri != null;
    const isZeroActivity = !hasReports;

    const hasBreaks = recon.breakCount > 0;
    const breaks = recon.breaks ?? [];

    const showFilesSection =
        hasReports && (status === 'PENDING_REVIEW' || isTerminal(status));
    const filesReadOnly = isTerminal(status);
    const showOperations =
        status === 'PENDING_REVIEW' || status === 'COMPLETED';
    const showRetrigger = status === 'PENDING_REVIEW' || status === 'FAILED';
    const showManualResolution = status === 'PENDING_REVIEW';

    // --- Dialogs ---

    function renderTriggerDialog() {
        return (
            <Dialog
                visible={showTriggerDialog}
                onHide={() => {
                    setShowTriggerDialog(false);
                    setIsDryRun(false);
                }}
                header="Re-run Reconciliation"
                style={{ width: '500px' }}
            >
                <YStack gap={16} padding={8}>
                    <BodyMediumRegular>
                        This will discard the current reconciliation and re-run
                        from scratch for{' '}
                        <BodyMediumEmphasized>
                            {settlementDate}
                        </BodyMediumEmphasized>
                        . Are you sure?
                    </BodyMediumRegular>
                    <XStack gap={8} alignItems="center">
                        <Checkbox
                            id="dry-run-toggle"
                            checked={isDryRun}
                            onCheckedChange={(checked) => {
                                setIsDryRun(checked === true);
                            }}
                        />
                        <BodySmallRegular>
                            Dry Run (preview only, no database changes)
                        </BodySmallRegular>
                    </XStack>
                    <XStack gap={12} justifyContent="flex-end">
                        <Button
                            variant="secondary"
                            size="small"
                            onPress={() => {
                                setShowTriggerDialog(false);
                                setIsDryRun(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="small"
                            tone="danger"
                            onPress={() => {
                                void handleTriggerRecon();
                            }}
                            disabled={triggerReconMutation.isPending}
                        >
                            {triggerReconMutation.isPending
                                ? 'Running...'
                                : isDryRun
                                  ? 'Run Dry Run'
                                  : 'Re-run Reconciliation'}
                        </Button>
                    </XStack>
                </YStack>
            </Dialog>
        );
    }

    function renderManualResolutionDialog() {
        return (
            <Dialog
                visible={showManualDialog}
                onHide={() => {
                    setShowManualDialog(false);
                    setManualNotes('');
                }}
                header="Resolve Manually"
                style={{ width: '500px' }}
            >
                <YStack gap={16} padding={8}>
                    <BodyMediumRegular>
                        Manually resolve the reconciliation for{' '}
                        <BodyMediumEmphasized>
                            {settlementDate}
                        </BodyMediumEmphasized>
                        .
                    </BodyMediumRegular>
                    <YStack gap={4}>
                        <BodySmallEmphasized>Admin Email</BodySmallEmphasized>
                        <BodySmallRegular color="$gray1000">
                            {adminUser.email}
                        </BodySmallRegular>
                    </YStack>
                    <YStack gap={4}>
                        <BodySmallEmphasized>
                            Review Notes (optional)
                        </BodySmallEmphasized>
                        <Input
                            placeholder="Notes about the manual resolution..."
                            value={manualNotes}
                            onChangeText={setManualNotes}
                        />
                    </YStack>
                    <XStack gap={12} justifyContent="flex-end">
                        <Button
                            variant="secondary"
                            size="small"
                            onPress={() => {
                                setShowManualDialog(false);
                                setManualNotes('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="small"
                            onPress={() => {
                                void handleManualResolution();
                            }}
                            disabled={manualResolutionMutation.isPending}
                        >
                            {manualResolutionMutation.isPending
                                ? 'Resolving...'
                                : 'Resolve Manually'}
                        </Button>
                    </XStack>
                </YStack>
            </Dialog>
        );
    }

    // --- Main render ---

    return (
        <YStack gap={24}>
            {/* Section 1: Header / Status Banner */}
            <YStack
                padding={20}
                borderRadius={12}
                borderWidth={1}
                borderColor={hasBreaks ? '#e57373' : '$gray200'}
                backgroundColor={hasBreaks ? '#ffebee' : '$gray100'}
                gap={12}
            >
                <XStack
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={8}
                >
                    <YStack gap={2}>
                        <H5Emphasized>
                            {new Date(
                                `${settlementDate}T12:00:00`,
                            ).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            })}
                        </H5Emphasized>
                        <FootnoteRegular
                            color="$gray1000"
                            fontFamily="monospace"
                        >
                            {recon.workflowId}
                        </FootnoteRegular>
                    </YStack>
                    <XStack gap={8} alignItems="center">
                        <StatusBadge status={status} />
                        {hasBreaks && (
                            <XStack
                                backgroundColor="#dc354518"
                                paddingHorizontal={12}
                                paddingVertical={4}
                                borderRadius={14}
                                borderWidth={1.5}
                                borderColor="#dc3545"
                            >
                                <BodySmallEmphasized color="#dc3545">
                                    {recon.breakCount} break
                                    {recon.breakCount !== 1 ? 's' : ''}
                                </BodySmallEmphasized>
                            </XStack>
                        )}
                    </XStack>
                </XStack>
                <XStack gap={8} alignItems="center">
                    <BodyMediumEmphasized>
                        {centsToFormattedDollars(recon.netAmountCents ?? 0)}
                    </BodyMediumEmphasized>
                    {recon.wireDirection != null && (
                        <BodyMediumRegular color="$gray1000">
                            {getWireDirectionLabel(recon.wireDirection)}
                        </BodyMediumRegular>
                    )}
                </XStack>
                {recon.reviewedBy != null && (
                    <BodySmallRegular color="$gray1000">
                        Reviewed by {recon.reviewedBy} at{' '}
                        {formatTimestamp(recon.reviewedAt)}
                    </BodySmallRegular>
                )}
                {isTerminal(status) && (
                    <BodySmallRegular color="$gray1000">
                        Completed: {formatTimestamp(recon.updatedAt)}
                    </BodySmallRegular>
                )}

                {/* Break Details Table */}
                {hasBreaks && breaks.length > 0 && (
                    <YStack
                        gap={8}
                        marginTop={4}
                        padding={12}
                        borderRadius={8}
                        backgroundColor="white"
                        borderWidth={1}
                        borderColor="#e57373"
                    >
                        <BodySmallEmphasized color="#dc3545">
                            Break Details ({breaks.length})
                        </BodySmallEmphasized>
                        <DataTable
                            value={breaks}
                            size="small"
                            tableStyle={{ fontSize: '0.75rem' }}
                        >
                            <Column
                                field="externalId"
                                header="External ID"
                                style={{ fontSize: '0.7rem' }}
                                body={(row: ReconBreakDto) => (
                                    <BodySmallRegular
                                        fontFamily="monospace"
                                        fontSize="0.7rem"
                                    >
                                        {row.externalId}
                                    </BodySmallRegular>
                                )}
                            />
                            <Column
                                field="source"
                                header="Source"
                                style={{ fontSize: '0.7rem' }}
                            />
                            <Column
                                field="breakReason"
                                header="Reason"
                                style={{ fontSize: '0.7rem' }}
                            />
                            <Column
                                field="amount"
                                header="Amount"
                                style={{ fontSize: '0.7rem' }}
                                body={(row: ReconBreakDto) =>
                                    `$${row.amount.toFixed(2)}`
                                }
                            />
                            <Column
                                field="transferDirection"
                                header="Direction"
                                style={{ fontSize: '0.7rem' }}
                            />
                        </DataTable>
                    </YStack>
                )}
            </YStack>

            {/* Section 2: Reconciliation Files (only for normal batch days with reports) */}
            {showFilesSection && (
                <YStack gap={12}>
                    <H6Emphasized>Reconciliation Files</H6Emphasized>
                    <XStack gap={16}>
                        {/* Apex Report */}
                        <YStack
                            flex={1}
                            padding={16}
                            borderRadius={12}
                            borderWidth={1}
                            borderColor={
                                apexSftpDone ? '$green300' : '$gray200'
                            }
                            backgroundColor={apexSftpDone ? '#f0fdf4' : 'white'}
                            gap={12}
                        >
                            <XStack
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <BodySmallEmphasized>
                                    Apex Report (Excel)
                                </BodySmallEmphasized>
                                {apexSftpDone ? (
                                    <Icon
                                        icon={CheckCircle2Icon}
                                        size={20}
                                        color="$green1000"
                                    />
                                ) : (
                                    <Icon
                                        icon={CircleIcon}
                                        size={20}
                                        color="$gray200"
                                    />
                                )}
                            </XStack>
                            {recon.apexReportS3Uri != null && (
                                <FootnoteRegular
                                    color="$gray1000"
                                    fontFamily="monospace"
                                >
                                    {recon.apexReportS3Uri}
                                </FootnoteRegular>
                            )}
                            {recon.apexReportUploadedToS3At != null && (
                                <FootnoteRegular color="$gray1000">
                                    S3 uploaded{' '}
                                    {formatTimestamp(
                                        recon.apexReportUploadedToS3At,
                                    )}
                                </FootnoteRegular>
                            )}
                            <XStack
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                {recon.apexReportS3Uri != null && (
                                    <Button
                                        variant="tertiary"
                                        size="tiny"
                                        icon={DownloadIcon}
                                        href={recon.apexReportS3Uri as Href}
                                        target="_blank"
                                    >
                                        Download
                                    </Button>
                                )}
                                {!filesReadOnly && (
                                    <>
                                        <input
                                            ref={apexFileInputRef}
                                            type="file"
                                            accept=".xlsx"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const file =
                                                    e.target.files?.[0];
                                                if (file != null) {
                                                    handleFileSelected(
                                                        'apex',
                                                        file,
                                                    );
                                                }
                                                e.target.value = '';
                                            }}
                                        />
                                        <Button
                                            variant="tertiary"
                                            size="tiny"
                                            icon={UploadIcon}
                                            onPress={() =>
                                                apexFileInputRef.current?.click()
                                            }
                                            disabled={
                                                uploadReconFileMutation.isPending
                                            }
                                        >
                                            Re-upload
                                        </Button>
                                    </>
                                )}
                            </XStack>
                        </YStack>

                        {/* Synctera Report */}
                        <YStack
                            flex={1}
                            padding={16}
                            borderRadius={12}
                            borderWidth={1}
                            borderColor={
                                syncteraSftpDone ? '$green300' : '$gray200'
                            }
                            backgroundColor={
                                syncteraSftpDone ? '#f0fdf4' : 'white'
                            }
                            gap={12}
                        >
                            <XStack
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <BodySmallEmphasized>
                                    Synctera Report (CSV)
                                </BodySmallEmphasized>
                                {syncteraSftpDone ? (
                                    <Icon
                                        icon={CheckCircle2Icon}
                                        size={20}
                                        color="$green1000"
                                    />
                                ) : (
                                    <Icon
                                        icon={CircleIcon}
                                        size={20}
                                        color="$gray200"
                                    />
                                )}
                            </XStack>
                            {recon.syncteraReportS3Uri != null && (
                                <FootnoteRegular
                                    color="$gray1000"
                                    fontFamily="monospace"
                                >
                                    {recon.syncteraReportS3Uri}
                                </FootnoteRegular>
                            )}
                            {recon.syncteraReportUploadedToS3At != null && (
                                <FootnoteRegular color="$gray1000">
                                    S3 uploaded{' '}
                                    {formatTimestamp(
                                        recon.syncteraReportUploadedToS3At,
                                    )}
                                </FootnoteRegular>
                            )}
                            <XStack
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                {recon.syncteraReportS3Uri != null && (
                                    <Button
                                        variant="tertiary"
                                        size="tiny"
                                        icon={DownloadIcon}
                                        href={recon.syncteraReportS3Uri as Href}
                                        target="_blank"
                                    >
                                        Download
                                    </Button>
                                )}
                                {!filesReadOnly && (
                                    <>
                                        <input
                                            ref={syncteraFileInputRef}
                                            type="file"
                                            accept=".csv"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const file =
                                                    e.target.files?.[0];
                                                if (file != null) {
                                                    handleFileSelected(
                                                        'synctera',
                                                        file,
                                                    );
                                                }
                                                e.target.value = '';
                                            }}
                                        />
                                        <Button
                                            variant="tertiary"
                                            size="tiny"
                                            icon={UploadIcon}
                                            onPress={() =>
                                                syncteraFileInputRef.current?.click()
                                            }
                                            disabled={
                                                uploadReconFileMutation.isPending
                                            }
                                        >
                                            Re-upload
                                        </Button>
                                    </>
                                )}
                            </XStack>
                        </YStack>
                    </XStack>
                </YStack>
            )}

            {/* Section 3: Operations Checklist */}
            {showOperations && (
                <YStack gap={12}>
                    <H6Emphasized>Operations Checklist</H6Emphasized>
                    {isZeroActivity && (
                        <BodySmallRegular color="$gray1000">
                            Zero-activity day — only email confirmation is
                            required.
                        </BodySmallRegular>
                    )}
                    <XStack gap={16} flexWrap="wrap">
                        <OperationCard
                            title="Wire Email (Gmail)"
                            icon={
                                <Icon
                                    icon={MailIcon}
                                    size={18}
                                    color="$gray1000"
                                />
                            }
                            isDone={emailDone}
                            timestamp={recon.emailSentAt}
                            actionLabel={
                                emailDone ? 'View Email' : 'Review & Compose'
                            }
                            onAction={() => {
                                setShowEmailModal(true);
                            }}
                            isPending={false}
                            disabled={false}
                            alwaysShowAction
                        />
                        {!isZeroActivity && (
                            <>
                                <OperationCard
                                    title="Upload to Apex SFTP"
                                    icon={
                                        <Icon
                                            icon={ServerIcon}
                                            size={18}
                                            color="$gray1000"
                                        />
                                    }
                                    isDone={apexSftpDone}
                                    timestamp={recon.apexSftpUploadedAt}
                                    detail={recon.apexSftpPath}
                                    actionLabel="Upload to Apex"
                                    onAction={() => {
                                        void handleUploadApex();
                                    }}
                                    isPending={uploadApexMutation.isPending}
                                    disabled={readonly}
                                />
                                <OperationCard
                                    title="Upload to Synctera SFTP"
                                    icon={
                                        <Icon
                                            icon={ServerIcon}
                                            size={18}
                                            color="$gray1000"
                                        />
                                    }
                                    isDone={syncteraSftpDone}
                                    timestamp={recon.syncteraSftpUploadedAt}
                                    detail={recon.syncteraSftpPath}
                                    actionLabel="Upload to Synctera"
                                    onAction={() => {
                                        void handleUploadSynctera();
                                    }}
                                    isPending={uploadSyncteraMutation.isPending}
                                    disabled={readonly}
                                />
                            </>
                        )}
                    </XStack>
                </YStack>
            )}

            {/* Section 4: Actions Footer */}
            {(showRetrigger || showManualResolution) && (
                <YStack gap={12}>
                    <H6Emphasized>Actions</H6Emphasized>
                    <XStack gap={12}>
                        {showRetrigger && (
                            <Button
                                variant="primary"
                                size="small"
                                icon={PlayIcon}
                                onPress={() => {
                                    setShowTriggerDialog(true);
                                }}
                            >
                                Re-run Reconciliation
                            </Button>
                        )}
                        {showManualResolution && (
                            <Button
                                variant="secondary"
                                size="small"
                                onPress={() => {
                                    setShowManualDialog(true);
                                }}
                            >
                                Resolve Manually
                            </Button>
                        )}
                    </XStack>
                </YStack>
            )}

            {/* Section 5: Debugging Details */}
            <YStack gap={12}>
                <H6Emphasized>Details</H6Emphasized>
                <YStack
                    gap={8}
                    padding={16}
                    borderRadius={12}
                    borderWidth={1}
                    borderColor="$gray200"
                    backgroundColor="$gray100"
                >
                    <XStack gap={24} flexWrap="wrap">
                        <YStack gap={2} minWidth={160}>
                            <BodySmallRegular color="$gray1000" fontSize={11}>
                                Created By
                            </BodySmallRegular>
                            <BodySmallEmphasized
                                fontFamily="monospace"
                                fontSize={12}
                            >
                                {recon.createdBy}
                            </BodySmallEmphasized>
                        </YStack>
                        <YStack gap={2} minWidth={160}>
                            <BodySmallRegular color="$gray1000" fontSize={11}>
                                Apex Batch ID
                            </BodySmallRegular>
                            <BodySmallEmphasized
                                fontFamily="monospace"
                                fontSize={12}
                            >
                                {recon.apexBatchId ?? '\u2014'}
                            </BodySmallEmphasized>
                        </YStack>
                        <YStack gap={2} minWidth={200}>
                            <BodySmallRegular color="$gray1000" fontSize={11}>
                                Workflow ID
                            </BodySmallRegular>
                            <BodySmallEmphasized
                                fontFamily="monospace"
                                fontSize={12}
                            >
                                {recon.workflowId}
                            </BodySmallEmphasized>
                        </YStack>
                        <YStack gap={2} minWidth={160}>
                            <BodySmallRegular color="$gray1000" fontSize={11}>
                                Current Step
                            </BodySmallRegular>
                            <BodySmallEmphasized
                                fontFamily="monospace"
                                fontSize={12}
                            >
                                {recon.currentStep ?? '\u2014'}
                            </BodySmallEmphasized>
                        </YStack>
                        <YStack gap={2} minWidth={160}>
                            <BodySmallRegular color="$gray1000" fontSize={11}>
                                Created At
                            </BodySmallRegular>
                            <BodySmallEmphasized fontSize={12}>
                                {formatTimestamp(recon.createdAt)}
                            </BodySmallEmphasized>
                        </YStack>
                        <YStack gap={2} minWidth={160}>
                            <BodySmallRegular color="$gray1000" fontSize={11}>
                                Updated At
                            </BodySmallRegular>
                            <BodySmallEmphasized fontSize={12}>
                                {formatTimestamp(recon.updatedAt)}
                            </BodySmallEmphasized>
                        </YStack>
                    </XStack>

                    {recon.error != null && (
                        <YStack gap={4} marginTop={8}>
                            <BodySmallEmphasized color="$red900">
                                Error
                            </BodySmallEmphasized>
                            <YStack
                                backgroundColor="#fff0f0"
                                padding={8}
                                borderRadius={6}
                                maxHeight={200}
                                overflow="scroll"
                                borderWidth={1}
                                borderColor="#ffcccc"
                            >
                                <FootnoteRegular
                                    fontFamily="monospace"
                                    whiteSpace="pre-wrap"
                                    wordWrap="break-word"
                                >
                                    {JSON.stringify(recon.error, null, 2)}
                                </FootnoteRegular>
                            </YStack>
                        </YStack>
                    )}
                </YStack>

                {/* State Log - Collapsible */}
                <YStack gap={8}>
                    <XStack
                        gap={8}
                        alignItems="center"
                        cursor="pointer"
                        onPress={() => {
                            setIsStateLogExpanded(!isStateLogExpanded);
                        }}
                        pressStyle={{ opacity: 0.7 }}
                    >
                        <Icon
                            icon={
                                isStateLogExpanded
                                    ? ChevronDownIcon
                                    : ChevronRightIcon
                            }
                            size={16}
                            color="$black"
                        />
                        <BodySmallEmphasized>
                            State Log ({recon.stateLog.length} entries)
                        </BodySmallEmphasized>
                    </XStack>

                    {isStateLogExpanded && recon.stateLog.length > 0 && (
                        <YStack
                            gap={8}
                            backgroundColor="$gray100"
                            padding={12}
                            borderRadius={8}
                            maxHeight={400}
                            overflow="scroll"
                        >
                            {recon.stateLog
                                .slice()
                                .sort(
                                    (a, b) =>
                                        new Date(b.timestamp).getTime() -
                                        new Date(a.timestamp).getTime(),
                                )
                                .map((entry, index) => (
                                    <YStack
                                        key={index}
                                        gap={4}
                                        padding={8}
                                        backgroundColor="white"
                                        borderRadius={4}
                                        borderWidth={1}
                                        borderColor="$gray200"
                                    >
                                        <XStack
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <BodySmallRegular
                                                fontWeight="bold"
                                                color="#2196F3"
                                            >
                                                {entry.step}
                                            </BodySmallRegular>
                                            <BodySmallRegular
                                                fontSize={10}
                                                color="$gray1000"
                                            >
                                                {new Date(
                                                    entry.timestamp,
                                                ).toLocaleString()}
                                            </BodySmallRegular>
                                        </XStack>
                                        <XStack justifyContent="space-between">
                                            <BodySmallRegular>
                                                Status:
                                            </BodySmallRegular>
                                            <BodySmallRegular
                                                fontWeight="bold"
                                                fontFamily="monospace"
                                            >
                                                {entry.status}
                                            </BodySmallRegular>
                                        </XStack>
                                        {entry.actor != null && (
                                            <XStack justifyContent="space-between">
                                                <BodySmallRegular>
                                                    Actor:
                                                </BodySmallRegular>
                                                <BodySmallRegular>
                                                    {entry.actor}
                                                </BodySmallRegular>
                                            </XStack>
                                        )}
                                        {entry.message != null && (
                                            <YStack gap={2}>
                                                <BodySmallRegular fontWeight="bold">
                                                    Message:
                                                </BodySmallRegular>
                                                <BodySmallRegular
                                                    fontSize={11}
                                                    color="$gray1100"
                                                >
                                                    {entry.message}
                                                </BodySmallRegular>
                                            </YStack>
                                        )}
                                    </YStack>
                                ))}
                        </YStack>
                    )}
                </YStack>
            </YStack>

            {renderTriggerDialog()}
            {renderManualResolutionDialog()}
            <Dialog
                visible={showReuploadConfirm != null}
                onHide={() => {
                    setShowReuploadConfirm(null);
                    setPendingFile(null);
                }}
                header="Confirm File Re-upload"
                style={{ width: '500px' }}
            >
                <YStack gap={16} padding={8}>
                    <BodyMediumRegular>
                        This will overwrite the existing{' '}
                        <BodyMediumEmphasized>
                            {showReuploadConfirm === 'apex'
                                ? 'Apex Excel'
                                : 'Synctera CSV'}
                        </BodyMediumEmphasized>{' '}
                        report for{' '}
                        <BodyMediumEmphasized>
                            {settlementDate}
                        </BodyMediumEmphasized>{' '}
                        in S3. The SFTP upload will need to be re-done after
                        replacing the file.
                    </BodyMediumRegular>
                    {pendingFile != null && (
                        <BodySmallRegular
                            color="$gray1000"
                            fontFamily="monospace"
                            fontSize={12}
                        >
                            {pendingFile.name} (
                            {(pendingFile.size / 1024).toFixed(1)} KB)
                        </BodySmallRegular>
                    )}
                    <XStack gap={12} justifyContent="flex-end">
                        <Button
                            variant="secondary"
                            size="small"
                            onPress={() => {
                                setShowReuploadConfirm(null);
                                setPendingFile(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="small"
                            tone="danger"
                            onPress={() => {
                                handleConfirmReupload();
                            }}
                            disabled={uploadReconFileMutation.isPending}
                        >
                            {uploadReconFileMutation.isPending
                                ? 'Uploading...'
                                : 'Replace File'}
                        </Button>
                    </XStack>
                </YStack>
            </Dialog>
            <WireEmailModal
                recon={recon}
                isOpen={showEmailModal}
                onClose={() => {
                    setShowEmailModal(false);
                }}
                onMarkSent={(overrides, review) => {
                    void handleMarkWireEmailSent(overrides, review);
                }}
                isMarkingSent={sendEmailMutation.isPending}
                adminUserEmail={adminUser.email}
            />
        </YStack>
    );
};
